/* ----------------------------------------------------------------------------
 * HandsFreeProvider — the app-wide home of "Hey Aura" (owner directive
 * 2026-07-24). Mounted ONCE inside the persistent AppShell (auth-protected
 * tree). It resolves the ACCOUNT mic-mode preference (bc_users, follows the
 * human) + the voice-availability probe, computes the wake gate, lifts the
 * shell-level Aura popup open-state, and — ONLY when the account is wake-
 * eligible — mounts the single HandsFreeEngineHost that arms the mic once and
 * survives every route change.
 *
 * 'standard' mic mode => the provider is inert: NO controller, NO wake engine,
 * NO popup, zero overhead (the product guarantee that standard never arms). The
 * wake ENGINE stays a lazy chunk: HandsFreeEngineHost (and its useHandsFree /
 * WakeWordEngine / ONNX graph) is imported lazily so the main bundle stays
 * wake-free for every standard-mode user.
 * -------------------------------------------------------------------------- */

import { Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { voiceBeacon } from '../net/voiceDiag.ts';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { getVoiceStatus } from '../net/voiceApi.ts';
import { getHandsFreePrefs } from '@/data/handsfree';
import type { VoiceProviderConfig } from '../providers/registry.ts';
import {
  HANDS_FREE_ENABLED,
  runtimeHandsFreeEnabled,
  effectiveRuntimeOverride,
  resolveWakeEnablement,
  deviceMicOff,
  setDeviceMicOff,
  type AuraMicMode,
} from './handsFreeConfig.ts';
import { useVoiceLease } from './useVoiceLease.ts';
import { shouldMountWakeEngine, shouldShowTakeoverPill } from './leaseMachine.ts';
import { VoiceLeaseTakeoverPill } from './VoiceLeaseTakeoverPill.tsx';

// LAZY: the wake engine host (and all wake/ONNX code it pulls) downloads ONLY
// when the account is wake-eligible — the main bundle stays 100% wake-free.
const HandsFreeEngineHost = lazy(() =>
  import('./HandsFreeEngineHost.tsx').then((m) => ({ default: m.HandsFreeEngineHost })),
);

interface HandsFreeContextValue {
  /** shell-level Aura popup open state. */
  auraOpen: boolean;
  /** open the app-wide Aura popup (any route). */
  openAura: () => void;
  /** close the app-wide Aura popup. */
  closeAura: () => void;
  /** the resolved account mic mode ('standard' until loaded / when unset). */
  micMode: AuraMicMode;
  /** the app-wide wake gate result (engine mounted only when true). */
  wakeEligible: boolean;
  /** the user killed the mic on THIS device (#8) — engine unmounted while true. */
  deviceMicOff: boolean;
  /** one-tap device mic kill (#8): persist + unmount the engine. Account mode untouched. */
  killDeviceMic: () => void;
  /** revive the mic on this device (Settings > Aura Voice affordance). */
  reviveDeviceMic: () => void;
}

const HandsFreeCtx = createContext<HandsFreeContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
/** Nullable variant for components that may render OUTSIDE the provider (tests,
 *  standalone settings) — returns null instead of throwing. */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalHandsFreeContext(): HandsFreeContextValue | null {
  return useContext(HandsFreeCtx);
}

export function useHandsFreeContext(): HandsFreeContextValue {
  const ctx = useContext(HandsFreeCtx);
  if (!ctx) throw new Error('useHandsFreeContext must be used within <HandsFreeProvider>');
  return ctx;
}

/** Can this browser actually run the wake pipeline? (getUserMedia + AudioContext) */
function browserSupportsWake(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    (typeof AudioContext !== 'undefined' ||
      typeof (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext !== 'undefined')
  );
}

export function HandsFreeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [auraOpen, setAuraOpen] = useState(false);
  const openAura = useCallback(() => setAuraOpen(true), []);
  const closeAura = useCallback(() => setAuraOpen(false), []);
  // #8 device mic kill: persisted per-device; gates the engine mount below.
  const [micKilled, setMicKilled] = useState(() => deviceMicOff());
  const killDeviceMic = useCallback(() => { setDeviceMicOff(true); setMicKilled(true); }, []);
  const reviveDeviceMic = useCallback(() => { setDeviceMicOff(false); setMicKilled(false); }, []);

  // ACCOUNT mic mode — user-scoped, safe-defaults to 'standard'.
  const micQuery = useQuery({
    queryKey: ['aura-handsfree', user?.id ?? null],
    queryFn: () => getHandsFreePrefs(),
    enabled: !!user,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const micMode: AuraMicMode = micQuery.data?.micMode ?? 'standard';

  // Voice availability probe (shared cache key with the launchers).
  const voiceQuery = useQuery({
    queryKey: ['voice-status'],
    queryFn: () => getVoiceStatus(),
    retry: false,
    staleTime: 60_000,
  });
  const voiceAvailable = voiceQuery.data?.voice_available === true;
  const voiceConfig: VoiceProviderConfig = {
    stt: voiceQuery.data?.stt_provider ?? 'mock',
    tts: voiceQuery.data?.tts_provider ?? 'mock',
    ttsVoiceId: voiceQuery.data?.tts_voice_id ?? 'aura-2-pandora-en',
  };

  const supported = useMemo(() => browserSupportsWake(), []);
  // CONSENT GUARD (owner directive 2026-07-25): the ?handsfree=1 URL override may
  // force wake-arm ONLY for an authenticated OWNER (self-consent) or in a NON-
  // production build (dev/staging). In production for a non-owner it is inert, so
  // it can never bypass the account-level (mic-mode) consent framework.
  // Owner decision 2026-07-26: the override is NON-PROD-ONLY — structurally
  // inert in production for everyone (owners included). isOwner no longer a factor.
  const isNonProd = import.meta.env.MODE !== 'production';
  const wakeEligible = resolveWakeEnablement({
    micMode,
    // explicit-Standard-wins (owner 2026-07-26): true only when the SERVER
    // preference has loaded — the safe default-fallback is not an explicit choice.
    modeExplicit: micQuery.data !== undefined,
    masterEnabled: HANDS_FREE_ENABLED,
    runtimeOverride: effectiveRuntimeOverride({
      overrideRequested: runtimeHandsFreeEnabled(),
      isNonProd,
    }),
    voiceAvailable,
    browserSupported: supported,
  });

  // CROSS-CLIENT ARBITRATION (voice residual #3): one human logged in on N
  // devices must get ONE armed wake engine. leaseActive = this client WOULD arm
  // if it held the lease; the lease then decides. The engine mounts only when
  // this client HOLDS the lease OR the arbiter is unreachable (fail-open = arm as
  // today). PTT (the manual Aura launcher) is never gated by this.
  const leaseActive = wakeEligible && !micKilled;
  const { gate: leaseGate, holderLabel, canArm, requestTakeover } = useVoiceLease(user?.id ?? null, leaseActive);
  const shouldMountEngine = shouldMountWakeEngine(leaseActive, canArm);
  const showTakeoverPill = shouldShowTakeoverPill(leaseActive, leaseGate);

  // WAKE-HOST GATE BEACON (owner nav-teardown report 2026-07-25): the ONLY
  // in-page path that unmounts the engine host (releasing the mic + engine) is
  // this gate. Beacon every input change so a mid-session mic drop names the
  // flapping input (diag-gated; strict no-op when the flag is off).
  useEffect(() => {
    voiceBeacon('wake_host_gate', {
      eligible: wakeEligible,
      mic_killed: micKilled,
      mic_mode: micMode,
      voice_available: voiceAvailable,
      supported,
      lease: leaseGate,
      lease_holder_is_self: leaseGate === 'held',
      mounted: shouldMountEngine,
    });
  }, [wakeEligible, micKilled, micMode, voiceAvailable, supported, leaseGate, shouldMountEngine]);

  const value = useMemo<HandsFreeContextValue>(
    () => ({ auraOpen, openAura, closeAura, micMode, wakeEligible, deviceMicOff: micKilled, killDeviceMic, reviveDeviceMic }),
    [auraOpen, openAura, closeAura, micMode, wakeEligible, micKilled, killDeviceMic, reviveDeviceMic],
  );

  return (
    <HandsFreeCtx.Provider value={value}>
      {children}
      {shouldMountEngine ? (
        <Suspense fallback={null}>
          <HandsFreeEngineHost
            userId={user?.id ?? null}
            voiceConfig={voiceConfig}
            auraOpen={auraOpen}
            openAura={openAura}
            closeAura={closeAura}
            onDeviceMicOff={killDeviceMic}
          />
        </Suspense>
      ) : null}
      {showTakeoverPill ? (
        <VoiceLeaseTakeoverPill holderLabel={holderLabel} onListenHere={requestTakeover} />
      ) : null}
    </HandsFreeCtx.Provider>
  );
}
